/**
 * A9HourCredit — hours staff grant to any customer, member or not (A9OS AN-1698).
 *
 * Credit is not a membership: it has its own metafield (`a9os.hour_credits`), its own slot field
 * (`hour_credit_consumable_hours`), its own hold flag (`hour_credit`) and its own cart property.
 * Nothing here reads or writes membership data.
 *
 * The theme never allocates credit itself. A9OS cuts a booking by grant and by day and spends the
 * least flexible grant first; mirroring that would be a second allocator to keep in sync. Before a
 * time is picked the metafield only says "credit applies"; after it, the hours come from the
 * backend — the slot grid, then the hold.
 *
 * Pure functions, node-testable; dates go through A9DateUtils.
 */
(function () {
  if (typeof window !== 'undefined' && window.A9HourCredit) return;

  const PERCENT_MAX = 100;
  const DISCOUNT_DECIMALS = 4;
  const SATURDAY = 6;
  const SUNDAY = 7;

  /**
   * Where the credit hours ride on the cart line for the SupaEasy function.
   * 'property'         → its own `_hour_credit` property (the function's second attribute key).
   * 'membership_hours' → inside `_membership_hours`, marked `source: 'credit'`, for when the
   *                      function cannot read a second attribute. It needs no function change.
   */
  // 'membership_hours' until SupaEasy says how its Second Key reaches the function input (AN-1699).
  const CARRIER = 'membership_hours';
  const PROPERTY_KEY = '_hour_credit';

  function root() {
    return typeof window !== 'undefined' ? window : globalThis;
  }

  function roundHours(value) {
    const utils = root().MembershipHoursUtils;
    return utils ? utils.roundHours(value) : Number(value) || 0;
  }

  function parseUtcMs(value) {
    if (typeof value !== 'string') return NaN;
    return Date.parse(value);
  }

  /** `null` = the backend did not say; `0` = it covers nothing. Never let `Number(null)` turn one into the other. */
  function readHours(value) {
    if (value === null || value === undefined || value === '') return null;
    const hours = Number(value);
    return Number.isFinite(hours) ? hours : null;
  }

  /** The metafield (array or JSON string) → valid grant rows; anything malformed is dropped. */
  function readGrants(raw) {
    let rows = raw;
    if (typeof raw === 'string') {
      try {
        rows = JSON.parse(raw);
      } catch (error) {
        console.warn('A9HourCredit: hour_credits metafield is not valid JSON', error);
        return [];
      }
    }
    if (!Array.isArray(rows)) return [];

    return rows.filter((row) => row
      && typeof row === 'object'
      && Number(row.hours) > 0
      && Number.isFinite(parseUtcMs(row.start))
      && Number.isFinite(parseUtcMs(row.end))
      && row.facility_id != null);
  }

  /** Grants this facility can spend: its own, plus any valid at every facility. */
  function grantsForFacility(grants, facilityId) {
    const id = Number(facilityId);
    return readGrants(grants).filter((grant) => grant.cross_facility === true || Number(grant.facility_id) === id);
  }

  function isWeekendYmd(ymd, tz) {
    const dateUtils = root().A9DateUtils;
    if (!dateUtils) return false;
    const weekday = dateUtils.weekday1to7(dateUtils.ymdToMdy(ymd, tz), tz);
    return weekday === SATURDAY || weekday === SUNDAY;
  }

  /**
   * Grants that may pay for something on this facility-local day — the "credit applies" preview
   * before a time is picked. Weekdays-only grants never pay a Saturday or Sunday start.
   * A9OS checks weekdays per segment, so a Friday booking running past midnight is only partly
   * covered; the hours after a time is picked come from the backend and already say so.
   */
  function grantsForDate(grants, facilityId, ymd, tz) {
    const dateUtils = root().A9DateUtils;
    if (!dateUtils || !ymd || !tz) return [];

    let bounds;
    try {
      bounds = dateUtils.dayBoundsZulu(ymd, tz);
    } catch (error) {
      return [];
    }
    const dayStartMs = parseUtcMs(bounds.from);
    const dayEndMs = parseUtcMs(bounds.to);
    const weekend = isWeekendYmd(ymd, tz);

    return grantsForFacility(grants, facilityId).filter((grant) => {
      if (weekend && grant.weekdays_only === true) return false;
      return parseUtcMs(grant.start) < dayEndMs && parseUtcMs(grant.end) > dayStartMs;
    });
  }

  /** `{ applies, weekdaysOnly }` for one day — no hour count: the backend alone allocates. */
  function coverageForDate(grants, facilityId, ymd, tz) {
    const covering = grantsForDate(grants, facilityId, ymd, tz);
    return {
      applies: covering.length > 0,
      weekdaysOnly: covering.length > 0 && covering.every((grant) => grant.weekdays_only === true)
    };
  }

  /**
   * `{ applies, weekdaysOnly }` for any future booking here — the page preview, which runs
   * before a date exists. A grant counts until it expires.
   */
  function coverageForFacility(grants, facilityId, nowMs) {
    const now = Number.isFinite(nowMs) ? nowMs : Date.now();
    const usable = grantsForFacility(grants, facilityId).filter((grant) => parseUtcMs(grant.end) > now);
    return {
      applies: usable.length > 0,
      weekdaysOnly: usable.length > 0 && usable.every((grant) => grant.weekdays_only === true)
    };
  }

  /** Credit hours A9OS will cover for one slot row of GET /slots. */
  function slotHours(slotRow) {
    if (!slotRow || typeof slotRow !== 'object') return null;
    return readHours(slotRow.hour_credit_consumable_hours);
  }

  /**
   * Which pool the hold asks for. The flags are exclusive and A9OS never falls back from one to
   * the other, so the slot's own figures decide: membership first, credit only where the
   * membership covers nothing. With no figures the old rule stands — ask for the membership
   * whenever one is known.
   */
  function pickHoldPool({ membershipHours, creditHours, membershipKnown, creditAllowed }) {
    const membership = readHours(membershipHours);
    const credit = readHours(creditHours);

    if (membership !== null && membership > 0) return 'membership';
    if (creditAllowed && credit !== null && credit > 0) return 'hour_credit';
    return membershipKnown ? 'membership' : null;
  }

  /** The hold's credit answer. A refusal still returns 201, so `applied` is the only verdict. */
  function readHold(holdResponse) {
    const result = { applied: false, hours: null, rejectionReason: null };
    if (!holdResponse) return result;
    result.applied = holdResponse.hour_credit === true;
    result.hours = readHours(holdResponse.hour_credit_consumable_hours);
    result.rejectionReason = holdResponse.hour_credit_rejection_reason || null;
    return result;
  }

  /**
   * Hours and the percent of the line they pay, in the shape the SupaEasy function reads.
   * `totalHours` is duration × suites — the backend figure already counts every suite.
   */
  function lineDiscount(hours, totalHours) {
    const total = Number(totalHours);
    const usedHours = roundHours(Math.max(0, Math.min(Number(hours) || 0, total > 0 ? total : 0)));
    const discount = total > 0 ? Number(((usedHours / total) * PERCENT_MAX).toFixed(DISCOUNT_DECIMALS)) : 0;
    return { usedHours, discount };
  }

  /** Price the line will carry once the credit is applied, in cents. */
  function discountedPriceCents(priceCents, hours, totalHours) {
    const { discount } = lineDiscount(hours, totalHours);
    return Math.max(0, Math.round(priceCents * (1 - discount / PERCENT_MAX)));
  }

  /**
   * Puts the credit on the reservation line — the single place that knows the carrier.
   * The `_membership_hours` carrier keeps its promo and tier entitlement either way.
   */
  function applyToLine(properties, hours, totalHours) {
    if (!properties) return null;
    const { usedHours, discount } = lineDiscount(hours, totalHours);
    if (!(usedHours > 0)) return null;

    if (CARRIER === 'membership_hours') {
      const carrier = properties._membership_hours;
      if (!carrier) {
        console.warn('A9HourCredit: no _membership_hours carrier on the line, credit not attached');
        return null;
      }
      Object.assign(carrier, { status: 'success', usedHours, discount, source: 'credit' });
      return carrier;
    }

    properties[PROPERTY_KEY] = { status: 'success', usedHours, discount };
    return properties[PROPERTY_KEY];
  }

  /** Whether credit hours on this line will reduce its price — whichever property carries them. */
  function lineHasCreditDiscount(properties) {
    if (!properties) return false;
    const carrier = CARRIER === 'membership_hours' ? properties._membership_hours : properties[PROPERTY_KEY];
    if (CARRIER === 'membership_hours' && (!carrier || carrier.source !== 'credit')) return false;
    return !!(carrier && carrier.status === 'success' && Number(carrier.discount) > 0);
  }

  const api = {
    CARRIER,
    PROPERTY_KEY,
    readGrants,
    grantsForFacility,
    grantsForDate,
    coverageForDate,
    coverageForFacility,
    slotHours,
    pickHoldPool,
    readHold,
    lineDiscount,
    discountedPriceCents,
    applyToLine,
    lineHasCreditDiscount
  };
  if (typeof window !== 'undefined') window.A9HourCredit = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
